import { v4 as uuid } from 'uuid';

import { createQueryString } from '@eika-infrastruktur/utils';

import type {
    ApprovalResult,
    PaymentItem,
    PaymentItemDetails,
    PaymentItemId,
    PaymentRequest,
    SignBulkPaymentRequest,
    SignSinglePaymentRequest,
    UpdatePaymentRequest,
} from '../generated-types';
import { OutboxWrapper } from '../models/outbox';
import { CONTEXT_PATH } from './index';
import { del, get, post, put } from './request';

export const SERVICE_PATH = 'rest/resource/outbox';

const mapPaymentItemToOutboxItem = (paymentItem: PaymentItem): OutboxWrapper => {
    const { canApprove } = paymentItem.permissions;
    const { probablyPSD2Payment } = paymentItem;
    const currentUserNotInApproversArray = paymentItem.approvers?.filter((x) => x.loggedInUser === true).length === 0;

    return {
        ...paymentItem,
        uniqueId: paymentItem.uniqueId ?? { value: uuid() },
        canBeSelected:
            ((canApprove && currentUserNotInApproversArray) || (paymentItem.approvers ?? []).length === 0) &&
            !probablyPSD2Payment,
        needSigningFromYou: canApprove && currentUserNotInApproversArray,
    };
};

export const getOutbox = async (): Promise<OutboxWrapper[]> => {
    const response = await get<PaymentItem[]>(`${CONTEXT_PATH}/${SERVICE_PATH}/`);
    return response.map(mapPaymentItemToOutboxItem);
};

export const getOutboxDetails = async (paymentId: PaymentItemId): Promise<PaymentItemDetails> =>
    get<PaymentItemDetails>(`${CONTEXT_PATH}/${SERVICE_PATH}/${paymentId.value}`);

export const createOutboxPayment = (
    request: PaymentRequest,
    acceptDuplicate = false,
): Promise<PaymentItem | undefined> => {
    const queryString = createQueryString({
        acceptDuplicate: acceptDuplicate.toString(),
    });
    return post<PaymentItem | undefined>(`${CONTEXT_PATH}/${SERVICE_PATH}${queryString}`, request);
};

export type UpdateOutboxPaymentRequest = {
    paymentId: PaymentItemId;
    request: UpdatePaymentRequest;
};

export const updateOutboxPayment = async ({ paymentId, request }: UpdateOutboxPaymentRequest): Promise<void> =>
    put<void>(`${CONTEXT_PATH}/${SERVICE_PATH}/${paymentId.value}`, request);

export const removeFromOutbox = (paymentId: PaymentItemId) => del(`${CONTEXT_PATH}/${SERVICE_PATH}/${paymentId.value}`);

export const unapproveOutboxItem = (paymentId: PaymentItemId) =>
    put(`${CONTEXT_PATH}/${SERVICE_PATH}/${paymentId.value}/unapprove`, {});

const signSingle = async (singlePayment: SignSinglePaymentRequest, queryString: string) =>
    post<ApprovalResult>(`${CONTEXT_PATH}/${SERVICE_PATH}/sign/single${queryString}`, singlePayment);

const signBulk = async (bulkPayment: SignBulkPaymentRequest, queryString: string) => {
    return post<ApprovalResult>(`${CONTEXT_PATH}/${SERVICE_PATH}/sign${queryString}`, bulkPayment);
};

type OutboxSigning =
    | { singlePayment: SignSinglePaymentRequest; bulkPayment?: never }
    | { singlePayment?: never; bulkPayment: SignBulkPaymentRequest };

export const sign = async ({ singlePayment, bulkPayment }: OutboxSigning) => {
    const queryString = createQueryString({
        deviceId: window.ReactNativeProps?.deviceId,
    });
    return singlePayment ? signSingle(singlePayment, queryString) : signBulk(bulkPayment, queryString);
};
